'use client';

import {
  getBrowserLanguages,
  getPreferredLocale,
} from '../../libs/browserLocale';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useRef } from 'react';

/**
 * Client-side locale detector.
 * On first mount it reads the browser's language preference and, if it maps to a
 * different supported locale than the one in the URL, swaps the locale segment and
 * reloads. Unsupported languages fall back to the default locale (handled by
 * getPreferredLocale). Runs once via useRef.
 */
export const BrowserLocaleDetector: FC = () => {
  const pathname = usePathname();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // Current locale = first URL segment (basePath is stripped by usePathname).
    const currentLocale = pathname.split('/')[1];
    const targetLocale = getPreferredLocale(getBrowserLanguages());
    if (targetLocale === currentLocale) return;

    // Swap the locale segment on the real URL so basePath, query string and hash
    // (e.g. the guest path's ?invitationId=&verificationToken=) are all preserved.
    const targetUrl = new URL(window.location.href);
    const urlSegments = targetUrl.pathname.split('/');
    const localeSegmentIndex = urlSegments.indexOf(currentLocale);
    if (localeSegmentIndex === -1) return;
    urlSegments[localeSegmentIndex] = targetLocale;
    targetUrl.pathname = urlSegments.join('/');
    window.location.replace(targetUrl.toString());
  }, [pathname]);

  return null;
};
